"use client";

import { useState, useMemo } from "react";
import { Plus } from "lucide-react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { DeleteDialog } from "@/components/molecules/DeleteDialog";
import { useAppSelector } from "@/store/hooks";
import { JsonListSection } from "./components";
import { TreeRow, LeafRow } from "./components/TreeRow";
import { useForm7Data, useForm7Handlers } from "./hooks";
import { INITIAL_STATE, DELETE_LABELS } from "./constants";
import type {
  FormProps,
  FormState,
  CategoryData,
  CategoryItemData,
  EditingSection,
} from "./types";

// ═══════════ MAIN FORM COMPONENT ═══════════

const Form7 = ({ selectedSection, onSave, pageId, pageComponentId, variantDetails }: FormProps) => {
  const [formState, setFormState] = useState<FormState>({
    ...INITIAL_STATE,
    expandedSemesterIds: new Set(),
    expandedCategoryIds: new Set(),
  });

  const variantConfig = variantDetails;
  const reduxStateForButton = useAppSelector((state) =>
    variantConfig?.selector ? variantConfig.selector(state.page as any) : null
  ) as Record<string, any> | null;

  // ═══════════ CONFIGS FROM VARIANT ═══════════
  const componentFieldsConfig = useMemo(() => variantConfig?.componentFields || [], [variantConfig?.componentFields]);
  const semesterFieldsConfig = useMemo(() => variantConfig?.semesterFieldsConfig || variantConfig?.categoryFieldsConfig || [], [variantConfig?.semesterFieldsConfig, variantConfig?.categoryFieldsConfig]);
  const categoryFieldsConfig = useMemo(() => variantConfig?.categoryFieldsConfig || [], [variantConfig?.categoryFieldsConfig]);
  const categoryItemFieldsConfig = useMemo(() => variantConfig?.categoryItemFieldsConfig || [], [variantConfig?.categoryItemFieldsConfig]);
  const subjectFieldsConfig = useMemo(() => variantConfig?.subjectFieldsConfig || variantConfig?.itemTableFieldsConfig || [], [variantConfig?.subjectFieldsConfig, variantConfig?.itemTableFieldsConfig]);
  const toolFieldsConfig = useMemo(() => variantConfig?.toolFieldsConfig || [], [variantConfig?.toolFieldsConfig]);
  const skillFieldsConfig = useMemo(() => variantConfig?.skillFieldsConfig || [], [variantConfig?.skillFieldsConfig]);
  const electiveFieldsConfig = useMemo(() => variantConfig?.electiveFieldsConfig || [], [variantConfig?.electiveFieldsConfig]);

  // ═══════════ DELETE DIALOG STATE ═══════════
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: EditingSection; id: number; name: string; parentId?: number } | null>(null);

  // ═══════════ HOOKS ═══════════
  const {
    reduxState,
    loadAllData,
    filteredSubjects,
    selectedSemesterCategories,
    availableCategoryItems,
  } = useForm7Data({
    selectedSection,
    pageComponentId,
    variantConfig,
    formState,
    setFormState,
    componentFieldsConfig,
  });

  const {
    handleComponentFieldsUpdate,
    handleToolsUpdate,
    handleSkillsUpdate,
    handleFormFieldsUpdate,
    handleComponentSave,
    handleSemesterClick,
    handleNewSemester,
    handleSemesterDelete,
    handleCategoryClick,
    handleNewCategory,
    handleCategoryDelete,
    handleCategoryItemClick,
    handleNewCategoryItem,
    handleCategoryItemDelete,
    handleSubjectClick,
    handleNewSubject,
    handleSubjectDelete,
    handleElectivesUpdate,
    handleSave,
    cancelEditing,
  } = useForm7Handlers({
    formState,
    setFormState,
    variantConfig,
    reduxState,
    pageId,
    pageComponentId,
    selectedSection,
    loadAllData,
    onSave,
    componentFieldsConfig,
    semesterFieldsConfig,
    categoryFieldsConfig,
    categoryItemFieldsConfig,
    subjectFieldsConfig,
    electiveFieldsConfig,
  });

  // ═══════════ DELETE DIALOG HELPERS ═══════════
  const openDeleteDialog = (type: EditingSection, id: number, name: string, parentId?: number) => {
    setDeleteTarget({ type, id, name, parentId });
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    switch (deleteTarget.type) {
      case "category": await handleCategoryDelete({ id: deleteTarget.id, semester_id: deleteTarget.parentId || 0, name: deleteTarget.name, items: [] }); break;
      case "categoryItem": await handleCategoryItemDelete({ id: deleteTarget.id, name: deleteTarget.name }); break;
      case "semester": await handleSemesterDelete({ id: deleteTarget.id, name: deleteTarget.name, categories: [] }); break;
      case "subject": await handleSubjectDelete({ id: deleteTarget.id, name: deleteTarget.name, semester_id: deleteTarget.parentId || 0 }); break;
    }
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const getDeleteDialogProps = () => {
    if (!deleteTarget) return { title: "", itemName: "", description: "" };
    const config = DELETE_LABELS[deleteTarget.type || ""] || { title: "Delete", description: "Are you sure?" };
    return { ...config, itemName: deleteTarget.name };
  };

  // ═══════════ EDITING FORM HELPERS ═══════════
  const getEditFormTitle = () => {
    switch (formState.editingSection) {
      case "category": return formState.editingId ? "Edit Category" : "Add New Category";
      case "categoryItem": return formState.editingId ? "Edit Category Item" : "Add New Category Item";
      case "semester": return formState.editingId ? "Edit Semester" : "Add New Semester";
      case "subject": return formState.editingId ? "Edit Subject" : "Add New Subject";
      default: return "";
    }
  };

  const getEditFieldsConfig = () => {
    switch (formState.editingSection) {
      case "category": return categoryFieldsConfig;
      case "categoryItem": return categoryItemFieldsConfig;
      case "semester": return semesterFieldsConfig;
      case "subject": return subjectFieldsConfig;
      default: return [];
    }
  };

  if (!variantConfig) return null;

  const { parentId } = formState;

  // ═══════════ RENDER ═══════════
  return (
    <div className="flex flex-col h-full">
      {/* ═══ 1. Component Settings ═══ */}
      {componentFieldsConfig.length > 0 && (
        <div className="mb-6 border-b pb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <span>📝</span> Component Settings
          </h3>
          <FieldsFormatter
            data={formState.componentFields}
            fields={componentFieldsConfig}
            updateParent={true}
            onChange={handleComponentFieldsUpdate}
            validationErrors={formState.componentFieldErrors}
            validationMessages={formState.componentFieldMessages}
            className="p-0 bg-transparent"
          />
        </div>
      )}

      {/* ═══ 2. Tools (JSON) ═══ */}
      {toolFieldsConfig.length > 0 && (
        <JsonListSection
          title="Tools"
          icon="🔧"
          items={formState.tools}
          fieldsConfig={toolFieldsConfig}
          onUpdate={handleToolsUpdate}
        />
      )}

      {/* ═══ 3. Skills (JSON) ═══ */}
      {skillFieldsConfig.length > 0 && (
        <JsonListSection
          title="Skills"
          icon="💡"
          items={formState.skills}
          fieldsConfig={skillFieldsConfig}
          onUpdate={handleSkillsUpdate}
        />
      )}

      {/* ═══ Save Component Button ═══ */}
      <div className="mb-6">
        <Button onClick={handleComponentSave} classType="admin" className="w-full">
          {reduxStateForButton?.id ? "Update Component" : "Save Component"}
        </Button>
      </div>

      {/* ═══ 4. Semesters with Categories & Items ═══ */}
      <SemestersSection
        formState={formState}
        setFormState={setFormState}
        parentId={parentId}
        pageComponentId={pageComponentId}
        semesterFieldsConfig={semesterFieldsConfig}
        onNewSemester={handleNewSemester}
        onSemesterClick={handleSemesterClick}
        onNewCategory={handleNewCategory}
        onCategoryClick={handleCategoryClick}
        onNewCategoryItem={handleNewCategoryItem}
        onCategoryItemClick={handleCategoryItemClick}
        onDeleteDialog={openDeleteDialog}
      />

      {/* ═══ 5. Subjects ═══ */}
      <SubjectsSection
        formState={formState}
        setFormState={setFormState}
        parentId={parentId}
        pageComponentId={pageComponentId}
        subjectFieldsConfig={subjectFieldsConfig}
        filteredSubjects={filteredSubjects}
        onNewSubject={handleNewSubject}
        onSubjectClick={handleSubjectClick}
        onDeleteDialog={openDeleteDialog}
      />

      {/* ═══ Not saved yet message ═══ */}
      {!parentId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-800 font-medium mb-2">Component Settings Not Saved Yet</p>
          <p className="text-blue-600 text-sm">
            Please save the Component Settings above before adding semesters, categories and subjects.
          </p>
        </div>
      )}

      {/* ═══ 6. Edit Form ═══ */}
      {formState.editingSection && parentId && pageComponentId && (
        <EditFormSection
          formState={formState}
          title={getEditFormTitle()}
          fieldsConfig={getEditFieldsConfig()}
          selectedSemesterCategories={selectedSemesterCategories}
          availableCategoryItems={availableCategoryItems}
          electiveFieldsConfig={electiveFieldsConfig}
          onFieldsUpdate={handleFormFieldsUpdate}
          onElectivesUpdate={handleElectivesUpdate}
          onSave={handleSave}
          onCancel={cancelEditing}
        />
      )}

      {/* ═══ Delete Dialog ═══ */}
      {deleteTarget && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          title={getDeleteDialogProps().title}
          itemName={getDeleteDialogProps().itemName}
          description={getDeleteDialogProps().description}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => { setIsDeleteDialogOpen(false); setDeleteTarget(null); }}
        />
      )}
    </div>
  );
};

export default Form7;

// ═══════════════════════════════════════════════════════════════════════
// SUB-SECTIONS (co-located to keep the file focused on rendering)
// ═══════════════════════════════════════════════════════════════════════

// ─── Semesters Section ───────────────────────────────────────────────

interface SemestersSectionProps {
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  parentId: number | null;
  pageComponentId?: string;
  semesterFieldsConfig: any[];
  onNewSemester: () => void;
  onSemesterClick: (sem: any) => void;
  onNewCategory: (semesterId: number) => void;
  onCategoryClick: (cat: CategoryData) => void;
  onNewCategoryItem: (categoryId: number) => void;
  onCategoryItemClick: (item: CategoryItemData, categoryId: number) => void;
  onDeleteDialog: (type: EditingSection, id: number, name: string, parentId?: number) => void;
}

function SemestersSection({
  formState,
  setFormState,
  parentId,
  pageComponentId,
  semesterFieldsConfig,
  onNewSemester,
  onSemesterClick,
  onNewCategory,
  onCategoryClick,
  onNewCategoryItem,
  onCategoryItemClick,
  onDeleteDialog,
}: SemestersSectionProps) {
  if (!parentId || !pageComponentId || !semesterFieldsConfig.length) return null;

  return (
    <div className="mb-6 border-t pt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <span>📅</span> Semesters, Categories & Items
        </h3>
        <button
          onClick={onNewSemester}
          className="flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New Semester
        </button>
      </div>

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 max-h-[28rem] overflow-y-auto">
        {formState.semesters.length > 0 ? (
          <div className="space-y-2">
            {formState.semesters.map((sem) => {
              const isSemExpanded = formState.expandedSemesterIds.has(sem.id);
              const isSemSelected = formState.editingSection === "semester" && formState.editingId === sem.id;
              const catCount = sem.categories?.length || 0;

              return (
                <div key={sem.id}>
                  <TreeRow
                    name={`${sem.name}${catCount > 0 ? ` (${catCount} categories)` : ""}`}
                    isExpanded={isSemExpanded}
                    isSelected={isSemSelected}
                    hasChildren={true}
                    icon="📅"
                    onToggle={() => setFormState((prev) => {
                      const s = new Set(prev.expandedSemesterIds);
                      s.has(sem.id) ? s.delete(sem.id) : s.add(sem.id);
                      return { ...prev, expandedSemesterIds: s };
                    })}
                    onClick={() => onSemesterClick(sem)}
                    onDelete={() => onDeleteDialog("semester", sem.id, sem.name)}
                    onAddChild={() => onNewCategory(sem.id)}
                    addChildLabel="Add Category"
                  />

                  {isSemExpanded && (
                    <div className="ml-6 mt-1 space-y-1">
                      {(sem.categories || []).map((cat) => {
                        const isCatExpanded = formState.expandedCategoryIds.has(cat.id);
                        const isCatSelected = formState.editingSection === "category" && formState.editingId === cat.id;

                        return (
                          <div key={cat.id}>
                            <TreeRow
                              name={cat.name}
                              isExpanded={isCatExpanded}
                              isSelected={isCatSelected}
                              hasChildren={true}
                              icon="📂"
                              onToggle={() => setFormState((prev) => {
                                const s = new Set(prev.expandedCategoryIds);
                                s.has(cat.id) ? s.delete(cat.id) : s.add(cat.id);
                                return { ...prev, expandedCategoryIds: s };
                              })}
                              onClick={() => onCategoryClick(cat)}
                              onDelete={() => onDeleteDialog("category", cat.id, cat.name, sem.id)}
                              onAddChild={() => onNewCategoryItem(cat.id)}
                              addChildLabel="Add Item"
                            />

                            {isCatExpanded && (
                              <div className="ml-8 mt-1 space-y-1">
                                {cat.items.map((item) => {
                                  const isItemSelected = formState.editingSection === "categoryItem" && formState.editingId === item.id;
                                  return (
                                    <LeafRow
                                      key={item.id}
                                      name={item.name}
                                      isSelected={isItemSelected}
                                      onClick={() => onCategoryItemClick(item, cat.id)}
                                      onDelete={() => onDeleteDialog("categoryItem", item.id, item.name, cat.id)}
                                    />
                                  );
                                })}
                                {cat.items.length === 0 && (
                                  <div className="text-xs text-gray-400 pl-5 py-1">No items yet</div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {(sem.categories || []).length === 0 && (
                        <div className="text-xs text-gray-400 pl-5 py-1">No categories yet. Click + to add one.</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 text-sm">
            No semesters yet. Click &quot;New Semester&quot; to add one.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subjects Section ────────────────────────────────────────────────

interface SubjectsSectionProps {
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  parentId: number | null;
  pageComponentId?: string;
  subjectFieldsConfig: any[];
  filteredSubjects: any[];
  onNewSubject: (semesterId: number) => void;
  onSubjectClick: (sub: any) => void;
  onDeleteDialog: (type: EditingSection, id: number, name: string, parentId?: number) => void;
}

function SubjectsSection({
  formState,
  setFormState,
  parentId,
  pageComponentId,
  subjectFieldsConfig,
  filteredSubjects,
  onNewSubject,
  onSubjectClick,
  onDeleteDialog,
}: SubjectsSectionProps) {
  if (!parentId || !pageComponentId || !subjectFieldsConfig.length || !formState.semesters.length) return null;

  return (
    <div className="mb-6 border-t pt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <span>📄</span> Subjects
        </h3>
      </div>

      {/* Semester filter tabs */}
      <div className="flex flex-wrap gap-2 mb-3">
        {formState.semesters.map((sem) => (
          <button
            key={sem.id}
            onClick={() => setFormState((prev) => ({ ...prev, selectedSemesterFilter: sem.id }))}
            className={`text-xs px-3 py-1.5 rounded transition-colors ${
              formState.selectedSemesterFilter === sem.id
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {sem.name}
          </button>
        ))}
      </div>

      {formState.selectedSemesterFilter && (
        <div className="mb-2">
          <button
            onClick={() => onNewSubject(formState.selectedSemesterFilter!)}
            className="flex items-center gap-1 text-sm px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Subject to {formState.semesters.find((s) => s.id === formState.selectedSemesterFilter)?.name}
          </button>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 max-h-72 overflow-y-auto">
        {filteredSubjects.length > 0 ? (
          <div className="space-y-1">
            {filteredSubjects.map((sub) => {
              const isSubSelected = formState.editingSection === "subject" && formState.editingId === sub.id;
              const sem = formState.semesters.find((s) => s.id === sub.semester_id);
              const catName = (sem?.categories || []).find((c: CategoryData) => c.id === sub.category_id)?.name;
              const itemName = (sem?.categories || [])
                .find((c: CategoryData) => c.id === sub.category_id)?.items
                .find((i: CategoryItemData) => i.id === sub.category_item_id)?.name;
              const subtitle = [catName, itemName, sub.credits ? `${sub.credits} credits` : ""].filter(Boolean).join(" · ");

              return (
                <LeafRow
                  key={sub.id}
                  name={sub.name}
                  subtitle={subtitle}
                  isSelected={isSubSelected}
                  onClick={() => onSubjectClick(sub)}
                  onDelete={() => onDeleteDialog("subject", sub.id, sub.name, sub.semester_id)}
                />
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 text-sm">
            {formState.selectedSemesterFilter
              ? "No subjects in this semester yet."
              : "Select a semester to view subjects."}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Edit Form Section ───────────────────────────────────────────────

interface EditFormSectionProps {
  formState: FormState;
  title: string;
  fieldsConfig: any[];
  selectedSemesterCategories: CategoryData[];
  availableCategoryItems: CategoryItemData[];
  electiveFieldsConfig: any[];
  onFieldsUpdate: (data: Record<string, any>) => void;
  onElectivesUpdate: (electives: any[]) => void;
  onSave: () => void;
  onCancel: () => void;
}

function EditFormSection({
  formState,
  title,
  fieldsConfig,
  selectedSemesterCategories,
  availableCategoryItems,
  electiveFieldsConfig,
  onFieldsUpdate,
  onElectivesUpdate,
  onSave,
  onCancel,
}: EditFormSectionProps) {
  return (
    <div className="border-t pt-6">
      <h4 className="font-semibold mb-4">{title}</h4>

      <FieldsFormatter
        data={formState.formFields}
        fields={fieldsConfig}
        updateParent={true}
        onChange={onFieldsUpdate}
        validationErrors={formState.formFieldErrors}
        validationMessages={formState.formFieldMessages}
        className="p-0 bg-transparent"
      />

      {/* Subject-specific: Semester, Category, Category Item dropdowns */}
      {formState.editingSection === "subject" && (
        <div className="mt-4 space-y-3">
          {/* Semester dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Semester *</label>
            <select
              value={formState.formFields.semester_id || ""}
              onChange={(e) => {
                const newSemId = Number(e.target.value);
                onFieldsUpdate({ ...formState.formFields, semester_id: newSemId, category_id: "", category_item_id: "" });
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select semester</option>
              {formState.semesters.map((sem) => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>

          {/* Category dropdown (categories from selected semester) */}
          {selectedSemesterCategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category (optional)</label>
              <select
                value={formState.formFields.category_id || ""}
                onChange={(e) => {
                  const catId = e.target.value ? Number(e.target.value) : "";
                  onFieldsUpdate({ ...formState.formFields, category_id: catId, category_item_id: "" });
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None</option>
                {selectedSemesterCategories.map((cat: CategoryData) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Category Item dropdown (only if category selected) */}
          {formState.formFields.category_id && availableCategoryItems.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category Item (optional)</label>
              <select
                value={formState.formFields.category_item_id || ""}
                onChange={(e) => onFieldsUpdate({ ...formState.formFields, category_item_id: e.target.value ? Number(e.target.value) : "" })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None</option>
                {availableCategoryItems.map((item: CategoryItemData) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Electives JSON section */}
          {electiveFieldsConfig.length > 0 && (
            <div className="mt-2">
              <JsonListSection
                title="Electives"
                icon="📚"
                items={formState.formFields.electives || []}
                fieldsConfig={electiveFieldsConfig}
                onUpdate={onElectivesUpdate}
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex gap-2">
        <Button onClick={onSave} classType="admin" className="flex-1">
          {formState.editingId ? "Update" : "Save"}
        </Button>
        <Button
          onClick={onCancel}
          classType="admin"
          className="px-4 bg-gray-200 text-gray-700 hover:bg-gray-300"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
